import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, User, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ClassSchedule {
  day: string;
  time: string;
  duration: number;
  class_type: string;
  instructor?: string;
  max_capacity?: number;
}

interface ClassSchedulesProps {
  schedules: ClassSchedule[];
}

const DAYS_ES: Record<string, string> = {
  monday: "Lunes",
  tuesday: "Martes",
  wednesday: "Miércoles",
  thursday: "Jueves",
  friday: "Viernes",
  saturday: "Sábado",
  sunday: "Domingo"
};

const CLASS_COLORS: Record<string, string> = {
  yoga: "bg-purple-100 text-purple-700 border-purple-200",
  pilates: "bg-pink-100 text-pink-700 border-pink-200",
  spinning: "bg-orange-100 text-orange-700 border-orange-200",
  zumba: "bg-yellow-100 text-yellow-700 border-yellow-200",
  crossfit: "bg-red-100 text-red-700 border-red-200",
  funcional: "bg-blue-100 text-blue-700 border-blue-200",
  boxeo: "bg-gray-100 text-gray-700 border-gray-200",
};

export const ClassSchedules = ({ schedules }: ClassSchedulesProps) => {
  if (!schedules || schedules.length === 0) return null;

  // Group schedules by day
  const schedulesByDay = schedules.reduce((acc, schedule) => {
    if (!acc[schedule.day]) {
      acc[schedule.day] = [];
    }
    acc[schedule.day].push(schedule);
    return acc;
  }, {} as Record<string, ClassSchedule[]>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Horario de Clases
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.entries(schedulesByDay).map(([day, daySchedules]) => (
            <div key={day} className="space-y-2">
              <h3 className="font-semibold text-primary">{DAYS_ES[day] || day}</h3>
              <div className="grid gap-2">
                {daySchedules
                  .sort((a, b) => a.time.localeCompare(b.time))
                  .map((schedule, index) => {
                    const colorClass = CLASS_COLORS[schedule.class_type.toLowerCase()] || CLASS_COLORS.funcional;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={colorClass}>
                              {schedule.class_type}
                            </Badge>
                            <span className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {schedule.time}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({schedule.duration} min)
                            </span>
                          </div>
                          {schedule.instructor && (
                            <p className="flex items-center gap-1 text-sm">
                              <User className="w-3 h-3" />
                              {schedule.instructor}
                            </p>
                          )}
                        </div>
                        {schedule.max_capacity && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>Máx. {schedule.max_capacity}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
